import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, BadgeCheck, Phone, Wrench } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { EASE } from "./Reveal";

const STATS = [
    { value: "Dealer", label: "accepted at franchise service counters" },
    { value: "$0", label: "large upfront payment — ever" },
    { value: "Monthly", label: "cancel-anytime subscription" },
    { value: "24/7", label: "roadside assistance included" },
];

export const Hero = ({ onQuote, scrollTo }) => {
    const content = useContent();
    const lines = [content.hero_line_1, content.hero_line_2, content.hero_line_3];
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "8%"]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative overflow-hidden pt-36 pb-16 md:pt-44 md:pb-24"
        >
            <div className="bg-grid absolute inset-0" aria-hidden="true" />
            <div
                className="absolute -top-48 right-[-12%] h-[620px] w-[620px] rounded-full bg-sky-500/10 blur-[150px]"
                aria-hidden="true"
            />
            <div
                className="pulse-line absolute left-0 top-0 h-px w-full bg-gradient-to-r from-transparent via-sky-400/60 to-transparent"
                aria-hidden="true"
            />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                        data-testid="hero-badge"
                        className="inline-flex items-center gap-2.5 rounded-full border border-sky-400/30 bg-sky-400/10 px-4 py-2"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-400" />
                        </span>
                        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-sky-300 sm:text-xs">
                            {content.hero_badge}
                        </span>
                    </motion.div>

                    <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
                        {lines.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${i === 2 ? "text-sky-400" : ""}`}
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        delay: 0.3 + i * 0.15,
                                        duration: 1.05,
                                        ease: EASE,
                                    }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg"
                    >
                        {content.hero_subtext}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.9, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-get-quote-btn"
                            onClick={onQuote}
                            className="group inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-7 py-4 text-base font-bold text-[#0A0D14] transition-[background-color,box-shadow,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_44px_rgba(56,189,248,0.5)] active:scale-95"
                        >
                            Get My Instant Quote
                            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            data-testid="hero-explore-btn"
                            onClick={() => scrollTo("#plans")}
                            className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 px-7 py-4 text-base font-semibold text-white transition-[border-color,background-color] duration-300 hover:border-sky-400/50 hover:bg-white/5"
                        >
                            Explore coverage
                            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                        </button>
                        <a
                            href="tel:+18886874889"
                            data-testid="hero-phone-link"
                            className="inline-flex items-center gap-2 text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-sky-300"
                        >
                            <Phone className="h-4 w-4 text-sky-400" />
                            Questions? (888) 687-4889
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.25, duration: 1 }}
                        data-testid="hero-stats"
                        className="mt-14 grid grid-cols-2 gap-x-6 gap-y-8 border-t border-white/10 pt-8 sm:grid-cols-4"
                    >
                        {STATS.map((s) => (
                            <div key={s.label}>
                                <p className="font-display text-2xl font-bold text-white">
                                    {s.value}
                                </p>
                                <p className="mt-1 text-xs leading-snug text-slate-500">
                                    {s.label}
                                </p>
                            </div>
                        ))}
                    </motion.div>
                </div>

                <div className="lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ delay: 0.55, duration: 1.15, ease: EASE }}
                        className="relative"
                    >
                        <div
                            className="absolute -inset-8 rounded-[3rem] bg-sky-500/10 blur-3xl"
                            aria-hidden="true"
                        />
                        <div
                            className="absolute left-1/2 top-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-sky-400/25 animate-[spin_45s_linear_infinite]"
                            aria-hidden="true"
                        />
                        <div
                            className="absolute left-1/2 top-1/2 h-[510px] w-[510px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-sky-400/10"
                            aria-hidden="true"
                        />
                        <motion.div
                            animate={{ y: [0, -14, 0] }}
                            transition={{
                                repeat: Infinity,
                                duration: 6,
                                ease: "easeInOut",
                            }}
                            className="relative py-8"
                        >
                            <motion.img
                                data-testid="hero-logo"
                                src="/logo-stacked.webp"
                                alt="AdvantaShield Warranty shield logo"
                                width="900"
                                height="600"
                                fetchpriority="high"
                                style={{ y: imgY }}
                                className="mx-auto w-full max-w-md drop-shadow-[0_0_70px_rgba(56,189,248,0.4)]"
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: -24 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.35, duration: 0.8, ease: EASE }}
                            data-testid="hero-chip-claim"
                            className="absolute -left-4 top-12 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#121722]/90 px-4 py-3 shadow-xl shadow-black/50 backdrop-blur-xl sm:-left-8"
                        >
                            <BadgeCheck className="h-5 w-5 text-sky-400" />
                            <div>
                                <p className="text-sm font-semibold text-white">
                                    Claim approved
                                </p>
                                <p className="text-xs text-slate-400">
                                    right at the counter
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 24 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.55, duration: 0.8, ease: EASE }}
                            data-testid="hero-chip-rate"
                            className="absolute -right-3 bottom-2 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#121722]/90 px-4 py-3 shadow-xl shadow-black/50 backdrop-blur-xl sm:-right-6"
                        >
                            <Wrench className="h-5 w-5 text-sky-400" />
                            <div>
                                <p className="text-sm font-semibold text-white">
                                    Retail labor rate
                                </p>
                                <p className="text-xs text-slate-400">
                                    paid to your dealer
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
